// League karmic system store. All state comes from backend responses —
// no karmic score math happens in Vue.
import { leagueApi } from '@/etc/leagueApi'

export default {
  namespaced: true,
  state: {
    me: null,
    users: [],
    posts: [],
    sectorDefinitions: [],
    loading: false,
    error: null,
    // Internal-testing toggle: show raw karmic scores next to ranks.
    showScores: process.env.NODE_ENV !== 'production',
  },
  getters: {
    rankedUsers: (state) => state.users,
    others: (state) =>
      state.users.filter((u) => u.id !== state.me?.id),
  },
  mutations: {
    me: (state, next) => {
      state.me = next
    },
    users: (state, next) => {
      state.users = next || []
    },
    posts: (state, next) => {
      state.posts = next || []
    },
    sectorDefinitions: (state, next) => {
      state.sectorDefinitions = next || []
    },
    loading: (state, next) => {
      state.loading = !!next
    },
    error: (state, next) => {
      state.error = next
    },
    showScores: (state, next) => {
      state.showScores = !!next
    },
  },
  actions: {
    refresh: async (store) => {
      const jwt = store.rootState.user.jwt
      if (!jwt) {
        store.commit('error', 'NOT_AUTHENTICATED')
        return false
      }
      store.commit('loading', true)
      store.commit('error', null)
      try {
        const [me, users, feed, sectors] = await Promise.all([
          leagueApi.me(jwt),
          leagueApi.users(jwt),
          leagueApi.feed(jwt),
          leagueApi.sectors(jwt),
        ])
        store.commit('me', me.user)
        store.commit('users', users.users)
        store.commit('posts', feed.posts)
        store.commit('sectorDefinitions', sectors.sectors)
        return true
      } catch (err) {
        console.error('league refresh failed', err)
        store.commit('error', err.message || 'LEAGUE_ERROR')
        return false
      } finally {
        store.commit('loading', false)
      }
    },

    // Runs a league API call, then refreshes everything so scores, ranks,
    // and feed state always reflect the backend's truth.
    act: async (store, { call, args = [] }) => {
      const jwt = store.rootState.user.jwt
      if (!jwt) {
        store.commit('error', 'NOT_AUTHENTICATED')
        return false
      }
      store.commit('error', null)
      try {
        await leagueApi[call](jwt, ...args)
        await store.dispatch('refresh')
        return true
      } catch (err) {
        console.error(`league ${call} failed`, err)
        store.commit('error', err.message || 'LEAGUE_ERROR')
        await store.dispatch('refresh')
        return false
      }
    },

    createPost: (store, content) =>
      store.dispatch('act', { call: 'createPost', args: [content] }),
    deletePost: (store, postId) =>
      store.dispatch('act', { call: 'deletePost', args: [postId] }),
    react: (store, { postId, type }) =>
      store.dispatch('act', { call: 'reactToPost', args: [postId, type] }),
    unreact: (store, { postId, type }) =>
      store.dispatch('act', { call: 'unreactToPost', args: [postId, type] }),
    comment: (store, { postId, content }) =>
      store.dispatch('act', { call: 'commentOnPost', args: [postId, content] }),
    deleteComment: (store, commentId) =>
      store.dispatch('act', { call: 'deleteComment', args: [commentId] }),
    follow: (store, userId) =>
      store.dispatch('act', { call: 'followUser', args: [userId] }),
    unfollow: (store, userId) =>
      store.dispatch('act', { call: 'unfollowUser', args: [userId] }),
    updateMySectors: (store, sectors) =>
      store.dispatch('act', { call: 'updateMySectors', args: [sectors] }),
  },
}
